import { MenuItem, type MenuItemProps } from "@rg/ui/components/MenuItem";
import { useCallback, useEffect } from "react";
import { clsx } from "clsx";
import { useAppStore } from "@/common/store/appStore";
import { useAccordion } from "@/common/hooks/useAccordion";
import { useActiveMenu } from "./hooks/useActiveMenu";
import ArrowIcon from "@/common/images/icons/chevron.svg?react";
import { Link } from "react-router-dom";
import "./BurgerMenuItem.scss";

interface IBurgerMenuItem extends MenuItemProps {
  subNav?: MenuItemProps[];
  delimiter?: boolean;
  onSetActiveMenuItem: (title: string) => void;
  activeMenuItem: string;
}

export const BurgerMenuItem = ({
  onClick,
  subNav,
  className,
  delimiter,
  onSetActiveMenuItem,
  activeMenuItem,
  ...item
}: IBurgerMenuItem) => {
  const toggleBurgerMenu = useAppStore((state) => state.toggleBurgerMenu);

  const { isRouteActive, hasActiveSubNav } = useActiveMenu(item.link, subNav);
  const { isExpanded, contentRef, toggleExpansion } = useAccordion(hasActiveSubNav);
  const isInternalLink = Boolean(item.link && !item.external && item.link.startsWith("/"));

  const handleCloseMenu = useCallback(() => {
    toggleBurgerMenu();
  }, [toggleBurgerMenu]);

  const onBurgerItemClick = useCallback(() => {
    if (onClick) {
      onClick();
    }

    if (!subNav) {
      handleCloseMenu();
      return;
    }

    if (!hasActiveSubNav) {
      onSetActiveMenuItem(item.title);
    }

    toggleExpansion();
  }, [subNav, onClick, handleCloseMenu, hasActiveSubNav, onSetActiveMenuItem, item.title, toggleExpansion]);

  useEffect(() => {
    if (isRouteActive) {
      onSetActiveMenuItem(item.title);
    }
  }, [isRouteActive, onSetActiveMenuItem, item.title]);

  const SubMenuArrowIcon = () => <ArrowIcon className="menuItemArrowIcon" />;

  return (
    <li key={item.title} aria-expanded={isExpanded} className={clsx(
      "burgerMenuItem",
      delimiter && "hasDelimiter",
      isExpanded && "dropActive",
      className
    )}>
      <MenuItem
        {...item}
        linkComponent={isInternalLink ? Link : item.linkComponent}
        linkComponentProp={isInternalLink ? "to" : item.linkComponentProp}
        className={clsx("menuItem", className)}
        rightIcon={subNav ? SubMenuArrowIcon : item.rightIcon}
        onClick={onBurgerItemClick}
        isActive={activeMenuItem === item.title}
      />
      {subNav && (
        <div ref={contentRef} className="burgerSubMenu" role="region">
          <ul>
            {subNav.map((item) =>
              <BurgerMenuItem
                {...item}
                key={item.title}
                onSetActiveMenuItem={onSetActiveMenuItem}
                activeMenuItem={activeMenuItem}
              />)}
          </ul>
        </div>
      )}
    </li>
  );
};
